import {
  Box,
  Card,
  CardActionArea,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Link as RouterLink } from 'react-router-dom';
import {
  DEFAULT_AWAY_COLOR,
  DEFAULT_HOME_COLOR,
  getSessionRoleLabel,
  type ReviewSession,
  type SessionMyRole,
} from '@/types';

export function formatReviewUpdated(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('zh-HK', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export function reviewSessionPath(session: Pick<ReviewSession, 'id' | 'share_group_id'>): string {
  const base = `/review/${session.id}`;
  if (session.share_group_id) return `${base}?group_id=${session.share_group_id}`;
  return base;
}

export function canDeleteReviewSession(session: ReviewSession): boolean {
  if (session.can_delete != null) return session.can_delete;
  return !!session.can_edit && session.my_role === 'owner' && !session.is_shared;
}

function ReviewSessionGroupCell({ session }: { session: ReviewSession }) {
  if (session.share_group_name) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <GroupsIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="body2" noWrap title={session.share_group_name} sx={{ minWidth: 0 }}>
          {session.share_group_name}
        </Typography>
      </Stack>
    );
  }
  if (session.is_shared && !session.is_group_share && !session.share_group_id && !session.share_group_name) {
    return (
      <Typography variant="body2" color="text.secondary">
        個人分享
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.disabled">
      —
    </Typography>
  );
}

function TeamColorDot({ color }: { color: string }) {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: color,
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.15)',
        flexShrink: 0,
        display: 'inline-block',
      }}
    />
  );
}

export function ReviewSessionMatchup({ session }: { session: ReviewSession }) {
  const homeColor = session.home_team_color || DEFAULT_HOME_COLOR;
  const awayColor = session.away_team_color || DEFAULT_AWAY_COLOR;

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap component="span">
      <Stack direction="row" alignItems="center" spacing={0.5} component="span">
        <TeamColorDot color={homeColor} />
        <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {session.home_team || '主隊'}
        </Typography>
      </Stack>
      <Typography component="span" variant="caption" color="text.secondary">
        vs
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} component="span">
        <TeamColorDot color={awayColor} />
        <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
          {session.away_team || '客隊'}
        </Typography>
      </Stack>
    </Stack>
  );
}

type ReviewSessionSetupFields = Pick<
  ReviewSession,
  'competition_name' | 'match_date' | 'home_team' | 'away_team' | 'home_team_color' | 'away_team_color'
>;

export function hasReviewSessionSetupInfo(session: ReviewSessionSetupFields): boolean {
  return !!(
    session.competition_name?.trim() ||
    session.match_date?.trim() ||
    session.home_team?.trim() ||
    session.away_team?.trim()
  );
}

export function ReviewSessionSetupLine({
  session,
  variant = 'body2',
}: {
  session: ReviewSessionSetupFields;
  variant?: 'body2' | 'body1';
}) {
  const segments: ReactNode[] = [];
  const competition = session.competition_name?.trim();
  const matchDate = session.match_date?.trim();
  const hasTeams = !!(session.home_team?.trim() || session.away_team?.trim());

  if (competition) segments.push(competition);
  if (matchDate) segments.push(matchDate);
  if (hasTeams) segments.push(<ReviewSessionMatchup key="matchup" session={session as ReviewSession} />);

  if (segments.length === 0) return null;

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ columnGap: 0.75, rowGap: 0.35 }}>
      {segments.map((segment, index) => (
        <Stack key={index} direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          {index > 0 && (
            <Typography component="span" variant={variant} color="text.secondary">
              ·
            </Typography>
          )}
          {typeof segment === 'string' ? (
            <Typography component="span" variant={variant} color="text.secondary">
              {segment}
            </Typography>
          ) : (
            segment
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export function ReviewSessionRoleChip({ role }: { role?: SessionMyRole | null }) {
  if (!role) return null;
  return (
    <Chip
      size="small"
      label={getSessionRoleLabel(role)}
      color={
        role === 'owner' ? 'secondary' : role === 'manager' ? 'warning' : role === 'participant' ? 'info' : 'default'
      }
      variant={role === 'viewer' ? 'outlined' : 'filled'}
      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
    />
  );
}

function ReviewSessionMeta({ session }: { session: ReviewSession }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {[session.match_date, `更新 ${formatReviewUpdated(session.updated_at)}`].filter(Boolean).join(' · ')}
    </Typography>
  );
}

function roleAccentColor(role?: SessionMyRole | null): 'secondary' | 'warning' | 'info' | 'default' {
  if (role === 'owner') return 'secondary';
  if (role === 'manager') return 'warning';
  if (role === 'participant') return 'info';
  return 'default';
}

interface ReviewSessionCardProps {
  session: ReviewSession;
  onDelete?: (e: React.MouseEvent, id: string) => void;
  compact?: boolean;
}

export function ReviewSessionCard({ session, onDelete, compact }: ReviewSessionCardProps) {
  const theme = useTheme();
  const accent = roleAccentColor(session.my_role);
  const path = reviewSessionPath(session);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          borderColor: accent === 'default' ? 'divider' : `${accent}.main`,
          boxShadow: 2,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardActionArea component={RouterLink} to={path} sx={{ alignItems: 'stretch' }}>
        <Stack direction="row" spacing={0} sx={{ minHeight: compact ? 88 : 96 }}>
          <Box
            sx={{
              width: compact ? 52 : 56,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t) =>
                alpha(
                  accent === 'default' ? t.palette.text.secondary : t.palette[accent].main,
                  t.palette.mode === 'dark' ? 0.16 : 0.12
                ),
              borderRight: '1px solid',
              borderColor: 'divider',
              color: accent === 'default' ? 'text.secondary' : `${accent}.main`,
            }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: compact ? 24 : 28 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, py: compact ? 1.25 : 1.5, pl: 1.5, pr: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={800} noWrap sx={{ mb: 0.35 }}>
                  {session.title}
                </Typography>
                {session.competition_name && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mb: 0.5 }}>
                    {session.competition_name}
                  </Typography>
                )}
                <ReviewSessionMatchup session={session} />
                {!compact && <Box sx={{ mt: 0.75 }}><ReviewSessionMeta session={session} /></Box>}
              </Box>
              <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0, mt: 0.25 }} />
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: compact ? 0.75 : 1 }}>
              <ReviewSessionRoleChip role={session.my_role} />
              {session.share_group_name && (
                <Chip
                  size="small"
                  icon={<GroupsIcon sx={{ fontSize: '14px !important' }} />}
                  label={session.share_group_name}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.68rem', maxWidth: '100%' }}
                />
              )}
              {session.is_shared && !session.is_group_share && !session.share_group_id && !session.share_group_name && (
                <Chip size="small" label="個人分享" variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
              )}
            </Stack>

            {compact && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                {session.match_date || '—'} · {formatReviewUpdated(session.updated_at)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardActionArea>

      {onDelete && canDeleteReviewSession(session) && (
        <Box
          sx={{
            px: 1,
            pb: 1,
            pt: 0.5,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.background.default, 0.4),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton size="small" color="error" onClick={(e) => onDelete(e, session.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Card>
  );
}

interface ReviewSessionTableProps {
  sessions: ReviewSession[];
  loading?: boolean;
  onRowClick: (session: ReviewSession) => void;
  onDelete?: (e: React.MouseEvent, id: string) => void;
}

export function ReviewSessionTable({ sessions, loading, onRowClick, onDelete }: ReviewSessionTableProps) {
  const colCount = 7;
  const colWidth = `${100 / colCount}%`;
  const cellSx = {
    py: 1.5,
    px: 1.5,
    width: colWidth,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'top' as const,
  };
  const headSx = { ...cellSx, fontWeight: 700 };

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        overflowX: 'auto',
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth: 880,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>場次</TableCell>
            <TableCell sx={headSx}>對陣</TableCell>
            <TableCell sx={headSx}>比賽日期</TableCell>
            <TableCell sx={headSx}>角色</TableCell>
            <TableCell sx={headSx}>群組</TableCell>
            <TableCell sx={headSx}>更新</TableCell>
            <TableCell align="right" sx={{ ...headSx, width: colWidth }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                載入中…
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((s) => {
              const accent = roleAccentColor(s.my_role);
              return (
                <TableRow
                  key={s.id}
                  hover
                  onClick={() => onRowClick(s)}
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 0 },
                    boxShadow: (t) =>
                      `inset 3px 0 0 ${
                        accent === 'default'
                          ? 'transparent'
                          : alpha(t.palette[accent].main, t.palette.mode === 'dark' ? 0.85 : 0.65)
                      }`,
                  }}
                >
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" fontWeight={700} noWrap title={s.title}>
                      {s.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block" title={s.competition_name || undefined}>
                      {s.competition_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <ReviewSessionMatchup session={s} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" noWrap>
                      {s.match_date || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <ReviewSessionRoleChip role={s.my_role} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <ReviewSessionGroupCell session={s} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {formatReviewUpdated(s.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={cellSx} onClick={(e) => e.stopPropagation()}>
                    {canDeleteReviewSession(s) && onDelete && (
                      <IconButton size="small" color="error" onClick={(e) => onDelete(e, s.id)} sx={{ mr: 0.25 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', verticalAlign: 'middle' }} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
